import React, { useMemo, useState } from "react";
import { ReactSVG } from "react-svg";
import arrowIcon from "../../assets/svg/arrow-s.svg";
import { copyTextToClipboardSafe } from "../APanel/CalendarPlus/utils";
import UIIcon from "../Common/UIIcon";
import UserAvatar from "../Common/UserAvatar";
import { pressPoint } from "./glass";
import SearchBarUI from "./SearchBarUI";

// evidențierea căutării: aceeași ca în toată aplicația (SearchBarUI.Highlight); clasa locului
// (highlightClassName, implicit studentItem__highlight) se adaugă peste ea
const highlight = (text, query, className) => <SearchBarUI.Highlight text={text} query={query} className={className} />;

/**
 * StudentItemUI — rândul unui om într-o listă (Studenți, Personal, Grupe, Mesaje, căutările):
 * avatarul (AvatarUI prin UserAvatar), numele, dedesubt telefonul (sau secondaryText) cu butonul
 * mic de copiere în culoarea stării programării și starea ca text, iar la capăt săgeata (sau
 * endAdornment). Liquid Glass: marginea de sticlă, lumina din punctul atins, focusul.
 * Stilurile: scss/UI/_studentItemUI.scss (clasele .studentItem*). Fostul Common/StudentItem,
 * care acum doar îl reexportă.
 *
 * student · avatarPerson (omul cu poza, când student e un rând construit) · color · initials
 * onOpen: clic / Enter / Spațiu · highlightQuery · highlightClassName
 * secondaryText: rândul de sub nume (implicit telefonul) · showChevron · endAdornment
 * phoneCopy: butonul de copiere a telefonului · statusText / statusTone (overdue | pending |
 *    booked) / statusTitle: starea programării · className
 */
export default function StudentItemUI({
  student,
  // avatarul (AvatarUI, prin UserAvatar): poza sau inițialele; avatarPerson =
  // obiectul cu poza, când `student` e doar un rând construit (Personal)
  avatarPerson,
  color,
  initials,
  onOpen,
  highlightQuery = "",
  secondaryText, // ex: phone
  showChevron = true,
  endAdornment = null,
  className = "",
  highlightClassName = "studentItem__highlight",
  // buton mic de copiere a telefonului, între număr și stare; fundalul lui
  // are culoarea stării și ține loc de separator
  phoneCopy = false,
  // starea programării, ca text simplu după buton („Întârziat")
  statusText = "",
  statusTone = null,
  statusTitle = "",
}) {
  const isInteractive = typeof onOpen === "function";
  const [copied, setCopied] = useState(false);

  const fullName = useMemo(() => {
    const fn = String(student?.firstName || "").trim();
    const ln = String(student?.lastName || "").trim();
    return `${fn} ${ln}`.trim();
  }, [student]);

  const secondary = secondaryText ?? student?.phone ?? "–";
  const phoneToCopy = String(student?.phone || "").trim();

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen?.(student);
    }
  };

  const handleCopyPhone = async (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!phoneToCopy) return;
    const ok = await copyTextToClipboardSafe(phoneToCopy);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  const statusLabel = String(statusText || "").trim();
  const copyTitle = copied ? "Copiat" : "Copiază telefonul";

  return (
    <div
      className={`studentItem ${className}`}
      onClick={isInteractive ? () => onOpen(student) : undefined}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
      onPointerDown={isInteractive ? pressPoint : undefined}
    >
      <UserAvatar
        person={avatarPerson || student}
        color={color}
        initials={initials}
        className="studentItem__avatar"
      />

      <div className="studentItem__info">
        <h3 className="studentItem__name">
          {highlight(fullName || "–", highlightQuery, highlightClassName)}
        </h3>
        <p className="studentItem__meta">
          <span className="studentItem__metaRow">
            <span className="studentItem__metaPhone">
              <span className="studentItem__metaPhoneText">
                {highlight(secondary, highlightQuery, highlightClassName)}
              </span>
              {phoneCopy && phoneToCopy ? (
                <button
                  type="button"
                  className={
                    "studentItem__copyBtn" +
                    (statusTone ? ` studentItem__copyBtn--${statusTone}` : "") +
                    (copied ? " is-copied" : "")
                  }
                  title={copyTitle}
                  aria-label={copied ? "Telefon copiat" : "Copiază telefonul"}
                  onClick={handleCopyPhone}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <UIIcon name="copy" className="studentItem__copyIcon" />
                </button>
              ) : null}
            </span>

            {statusLabel ? (
              <span
                className="studentItem__status"
                title={statusTitle || statusLabel}
              >
                {statusLabel}
              </span>
            ) : null}
          </span>
        </p>
      </div>

      {endAdornment ? (
        <div className="studentItem__end">{endAdornment}</div>
      ) : showChevron ? (
        <div className="studentItem__chev" aria-hidden="true">
          <ReactSVG className="studentItem__chevIcon" src={arrowIcon} />
        </div>
      ) : null}
    </div>
  );
}
